import { Maximize, Crosshair } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

// ---------------------------------------------------------------------------
// Manual fit button
// ---------------------------------------------------------------------------

interface FitToBoundsButtonProps {
  onClick: () => void;
}

export function FitToBoundsButton({ onClick }: FitToBoundsButtonProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className="flex h-7 w-7 items-center justify-center rounded-md bg-background/80 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            aria-label="Fit model to view"
          >
            <Maximize className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="flex items-center gap-2 border-border bg-background text-foreground"
        >
          <span>Fit model to view</span>
          <kbd className="rounded border border-border bg-muted px-1 font-mono text-xs text-muted-foreground">
            F
          </kbd>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Auto-fit toggle — enables continuous fit-to-bounds on every recompile
// ---------------------------------------------------------------------------

interface AutoFitToggleProps {
  isEnabled: boolean;
  onToggle: (enabled: boolean) => void;
}

export function AutoFitToggle({ isEnabled, onToggle }: AutoFitToggleProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onToggle(!isEnabled)}
            className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
              isEnabled
                ? 'bg-sky-500/20 text-sky-500 hover:bg-sky-500/30'
                : 'bg-background/80 text-muted-foreground hover:bg-background hover:text-foreground'
            }`}
            aria-label={
              isEnabled
                ? 'Disable auto-fit on recompile'
                : 'Enable auto-fit on recompile'
            }
          >
            <Crosshair className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="border-border bg-background text-foreground"
        >
          <p>{isEnabled ? 'Auto-fit enabled' : 'Auto-fit on recompile'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
